import type { MetricDefinition, PeriodId } from "@/grid/core/types";

/** Where targets are stored and edited. */
export type TargetStoragePeriod = "week_default" | "month";

/** Derive a grid column target from finer-grained stored targets. */
export type TargetRollupRule =
  "sum-weeks-in-month" | "sum-weeks-in-quarter" | "sum-months-in-quarter";

export interface TargetPolicy {
  setAt: TargetStoragePeriod;
  rollups?: Partial<Record<PeriodId, TargetRollupRule>>;
}

export interface TargetMetricConfig {
  department: string;
  metric: string;
  setAt: TargetStoragePeriod;
  inferMtdFromWeekly: boolean;
  inferQtdFromWeekly: boolean;
  inferQtdFromMonthly: boolean;
}

export const DEFAULT_WEEKLY_TARGET_POLICY: TargetPolicy = {
  setAt: "week_default",
  rollups: {
    mtd: "sum-weeks-in-month",
    qtd: "sum-weeks-in-quarter",
  },
};

export const DEFAULT_MONTHLY_TARGET_POLICY: TargetPolicy = {
  setAt: "month",
  rollups: {
    qtd: "sum-months-in-quarter",
  },
};

export function getCatalogTargetPolicy(metric: MetricDefinition): TargetPolicy {
  return metric.targetPolicy ?? DEFAULT_WEEKLY_TARGET_POLICY;
}

export function defaultMetricConfig(
  departmentId: string,
  metric: MetricDefinition,
): TargetMetricConfig {
  const catalog = getCatalogTargetPolicy(metric);
  return {
    department: departmentId,
    metric: metric.targetMetric,
    setAt: catalog.setAt,
    inferMtdFromWeekly: catalog.rollups?.mtd === "sum-weeks-in-month",
    inferQtdFromWeekly: catalog.rollups?.qtd === "sum-weeks-in-quarter",
    inferQtdFromMonthly: catalog.rollups?.qtd === "sum-months-in-quarter",
  };
}

export function buildEffectiveTargetPolicy(
  _metric: MetricDefinition,
  config: TargetMetricConfig,
): TargetPolicy {
  const rollups: Partial<Record<PeriodId, TargetRollupRule>> = {};

  if (config.setAt === "week_default") {
    if (config.inferMtdFromWeekly) rollups.mtd = "sum-weeks-in-month";
    if (config.inferQtdFromWeekly) rollups.qtd = "sum-weeks-in-quarter";
  } else {
    if (config.inferQtdFromMonthly) rollups.qtd = "sum-months-in-quarter";
  }

  return { setAt: config.setAt, rollups };
}

export function indexMetricConfigs(
  departmentId: string,
  metrics: MetricDefinition[],
  rows: TargetMetricConfig[],
): Map<string, TargetMetricConfig> {
  const byMetric = new Map(rows.map((row) => [row.metric, row]));
  const result = new Map<string, TargetMetricConfig>();

  for (const metric of metrics) {
    result.set(
      metric.targetMetric,
      byMetric.get(metric.targetMetric) ?? defaultMetricConfig(departmentId, metric),
    );
  }

  return result;
}

export function getEffectiveTargetPolicy(
  metric: MetricDefinition,
  configByMetric: Map<string, TargetMetricConfig>,
): TargetPolicy {
  const config = configByMetric.get(metric.targetMetric);
  if (!config) return getCatalogTargetPolicy(metric);
  return buildEffectiveTargetPolicy(metric, config);
}

export function metricsForStorage(
  metrics: MetricDefinition[],
  setAt: TargetStoragePeriod,
  configByMetric: Map<string, TargetMetricConfig>,
): MetricDefinition[] {
  return metrics.filter(
    (metric) => getEffectiveTargetPolicy(metric, configByMetric).setAt === setAt,
  );
}

export function metricsNeedingExplicitMonth(
  metrics: MetricDefinition[],
  configByMetric: Map<string, TargetMetricConfig>,
): MetricDefinition[] {
  return metrics.filter((metric) => {
    const config = configByMetric.get(metric.targetMetric)!;
    return config.setAt === "week_default" && !config.inferMtdFromWeekly;
  });
}

export function metricsNeedingExplicitQuarter(
  metrics: MetricDefinition[],
  configByMetric: Map<string, TargetMetricConfig>,
): MetricDefinition[] {
  return metrics.filter((metric) => {
    const config = configByMetric.get(metric.targetMetric)!;
    if (config.setAt === "week_default") return !config.inferQtdFromWeekly;
    return !config.inferQtdFromMonthly;
  });
}

export function rollupRuleForColumn(
  metric: MetricDefinition,
  periodId: PeriodId,
  configByMetric: Map<string, TargetMetricConfig>,
): TargetRollupRule | null {
  const policy = getEffectiveTargetPolicy(metric, configByMetric);
  return policy.rollups?.[periodId] ?? null;
}

export function columnUsesWeeklyLookup(
  metric: MetricDefinition,
  periodId: PeriodId,
  configByMetric: Map<string, TargetMetricConfig>,
): boolean {
  const policy = getEffectiveTargetPolicy(metric, configByMetric);
  if (policy.setAt !== "week_default") return false;
  return periodId === "wtd" || periodId === "lastWeek";
}

export function columnUsesMonthlyLookup(
  metric: MetricDefinition,
  periodId: PeriodId,
  configByMetric: Map<string, TargetMetricConfig>,
): boolean {
  if (periodId !== "mtd") return false;
  const config = configByMetric.get(metric.targetMetric)!;
  if (config.setAt === "month") return true;
  return config.setAt === "week_default" && !config.inferMtdFromWeekly;
}

export function columnUsesQuarterlyLookup(
  metric: MetricDefinition,
  periodId: PeriodId,
  configByMetric: Map<string, TargetMetricConfig>,
): boolean {
  if (periodId !== "qtd") return false;
  const config = configByMetric.get(metric.targetMetric)!;
  if (config.setAt === "week_default") return !config.inferQtdFromWeekly;
  return !config.inferQtdFromMonthly;
}
